import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage } from '../../lib/api';
import { DataTable } from '../ui/DataTable';
import type { Column } from '../ui/DataTable';
import { toast } from '../../utils/toast';
import type { Plan } from '../../types';
import { Calendar, DollarSign, RefreshCw, Plus } from 'lucide-react';

interface ProductPlansProps {
  productId: string;
}

export const ProductPlans: React.FC<ProductPlansProps> = ({ productId }) => {
  const queryClient = useQueryClient();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [formData, setFormData] = useState<Omit<Plan, 'id'>>({
    product_id: productId,
    tier: 'basic',
    monthly_quota: 1000,
    rate_limit_per_minute: 60,
    monthly_price: 0,
    per_transaction_price: 0,
    percentage_fee: 0,
    min_transaction_fee: 0,
    max_transaction_fee: 0,
    max_users: 0,
    max_volume_usd: 0,
    pricing_model: 'monthly',
  });

  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(10);

  // Close modals on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const { data: plans, isLoading } = useQuery({
    queryKey: ['plans', productId],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: Plan[] }>(
        `/admin/products/${productId}/plans`
      );
      return response.data.data;
    },
    enabled: !!productId,
  });

  const allPlans = plans || [];
  const paginatedPlans = allPlans.slice(offset, offset + limit);

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await api.post<{ success: boolean; data: Plan }>(
        `/admin/products/${data.product_id}/plans`,
        data
      );
      return response.data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['plans'] });
      closeModal();
      toast.success('Plano criado com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao criar plano.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: { id: string } & typeof formData) => {
      const { id, ...updateData } = data;
      const response = await api.put<{ success: boolean; data: Plan }>(
        `/admin/plans/${id}`,
        updateData
      );
      return response.data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['plans'] });
      closeModal();
      toast.success('Plano atualizado com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao atualizar plano.');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingPlan) {
      updateMutation.mutate({ id: editingPlan.id, ...formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (plan: Plan) => {
    setEditingPlan(plan);
    setFormData({
      product_id: plan.product_id,
      tier: plan.tier,
      monthly_quota: plan.monthly_quota,
      rate_limit_per_minute: plan.rate_limit_per_minute,
      monthly_price: plan.monthly_price,
      per_transaction_price: plan.per_transaction_price,
      percentage_fee: plan.percentage_fee || 0,
      min_transaction_fee: plan.min_transaction_fee || 0,
      max_transaction_fee: plan.max_transaction_fee || 0,
      max_users: plan.max_users || 0,
      max_volume_usd: plan.max_volume_usd || 0,
      pricing_model: plan.pricing_model,
    });
    setShowCreateModal(true);
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingPlan(null);
    setFormData({
      product_id: productId,
      tier: 'basic',
      monthly_quota: 1000,
      rate_limit_per_minute: 10,
      monthly_price: 0,
      per_transaction_price: 0,
      percentage_fee: 0,
      min_transaction_fee: 0,
      max_transaction_fee: 0,
      max_users: 0,
      max_volume_usd: 0,
      pricing_model: 'monthly',
    });
  };

  const getPricingModelBadge = (model: string) => {
    const badges = {
      monthly: 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-400',
      per_transaction:
        'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400',
      hybrid: 'bg-purple-100 text-purple-800 dark:bg-purple-950/40 dark:text-purple-400',
    };
    return (
      badges[model as keyof typeof badges] ||
      'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300'
    );
  };

  const getPricingDisplay = (plan: Plan) => {
    switch (plan.pricing_model) {
      case 'monthly':
        return (
          <div>
            <div className="font-semibold text-slate-900 dark:text-white">
              ${plan.monthly_price}/mês
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              {plan.monthly_quota.toLocaleString()} pedidos incluídos
            </div>
          </div>
        );
      case 'per_transaction':
        return (
          <div>
            <div className="font-semibold text-slate-900 dark:text-white">
              ${plan.per_transaction_price}/pedido
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              Pagamento por transação
            </div>
          </div>
        );
      case 'hybrid':
        return (
          <div>
            <div className="font-semibold text-slate-900 dark:text-white">
              ${plan.monthly_price}/mês
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              + ${plan.per_transaction_price}/pedido após {plan.monthly_quota.toLocaleString()}
            </div>
          </div>
        );
      default:
        return <div className="text-slate-500 dark:text-slate-400">-</div>;
    }
  };

  const columns: Column<Plan>[] = [
    {
      key: 'tier',
      header: 'Nível',
      render: (plan) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 capitalize">
          {plan.tier}
        </span>
      ),
    },
    {
      key: 'pricing_model',
      header: 'Modelo de Preço',
      render: (plan) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getPricingModelBadge(plan.pricing_model)}`}
        >
          {plan.pricing_model.replace('_', ' ')}
        </span>
      ),
    },
    {
      key: 'pricing',
      header: 'Preço',
      render: (plan) => getPricingDisplay(plan),
    },
    {
      key: 'monthly_quota',
      header: 'Quota Mensal',
      render: (plan) => <span>{plan.monthly_quota.toLocaleString()}</span>,
    },
    {
      key: 'rate_limit',
      header: 'Limite de Débito',
      render: (plan) => <span>{plan.rate_limit_per_minute}/min</span>,
    },
    {
      key: 'actions',
      header: 'Ações',
      render: (plan) => (
        <button
          onClick={() => handleEdit(plan)}
          className="text-primary-600 dark:text-primary-400 hover:text-primary-700 text-sm font-medium"
        >
          Editar
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">Planos de Subscrição</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Configure os níveis de acesso e preços para este produto
          </p>
        </div>
        <button
          className="btn-primary inline-flex items-center gap-1.5"
          onClick={() => setShowCreateModal(true)}
        >
          <Plus className="w-4 h-4" />
          <span>Criar Plano</span>
        </button>
      </div>

      <DataTable
        columns={columns}
        data={paginatedPlans}
        isLoading={isLoading}
        total={allPlans.length}
        limit={limit}
        offset={offset}
        onPageChange={setOffset}
        onLimitChange={setLimit}
        emptyTitle="Nenhum plano configurado"
        emptyDescription="Comece por criar o primeiro plano para os seus produtos."
        emptyAction={
          <button onClick={() => setShowCreateModal(true)} className="btn-primary">
            Criar Primeiro Plano
          </button>
        }
      />

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeModal();
            }
          }}
        >
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-200 dark:border-slate-800">
            <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-white">
              {editingPlan ? 'Editar Plano' : 'Criar Novo Plano'}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              {(createMutation.isError || updateMutation.isError) && (
                <div className="rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/50 p-3">
                  <p className="text-sm text-rose-800 dark:text-rose-300">
                    {getErrorMessage(createMutation.error || updateMutation.error)}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="tier" className="label">
                    Nível *
                  </label>
                  <select
                    id="tier"
                    required
                    className="select"
                    value={formData.tier}
                    onChange={(e) => setFormData({ ...formData, tier: e.target.value as any })}
                  >
                    <option value="basic">Basic</option>
                    <option value="business">Business</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="pricing_model" className="label">
                    Modelo de Preço *
                  </label>
                  <select
                    id="pricing_model"
                    required
                    className="select"
                    value={formData.pricing_model}
                    onChange={(e) =>
                      setFormData({ ...formData, pricing_model: e.target.value as any })
                    }
                  >
                    <option value="monthly">Mensal - Valor mensal fixo</option>
                    <option value="per_transaction">Por Transação - Pagamento por transação</option>
                    <option value="hybrid">Híbrido - Mensal + Transação</option>
                  </select>
                </div>
              </div>

              {/* Pricing Model Explanation */}
              <div className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/50 rounded-xl">
                {formData.pricing_model === 'monthly' && (
                  <div className="text-sm text-blue-800 dark:text-blue-200 flex items-start gap-2">
                    <Calendar className="w-4 h-4 shrink-0 mt-0.5" />
                    <div>
                      <strong>Mensal:</strong> Taxa mensal fixa. Inclui até{' '}
                      {formData.monthly_quota.toLocaleString()} pedidos de API. Sem custos extra por
                      pedido.
                    </div>
                  </div>
                )}
                {formData.pricing_model === 'per_transaction' && (
                  <div className="text-sm text-emerald-800 dark:text-emerald-200 flex items-start gap-2">
                    <DollarSign className="w-4 h-4 shrink-0 mt-0.5" />
                    <div>
                      <strong>Por Transação:</strong> Pague por cada pedido de API. Sem taxa mensal
                      fixa. Pedidos ilimitados (conforme saldo de carteira).
                    </div>
                  </div>
                )}
                {formData.pricing_model === 'hybrid' && (
                  <div className="text-sm text-purple-800 dark:text-purple-200 flex items-start gap-2">
                    <RefreshCw className="w-4 h-4 shrink-0 mt-0.5" />
                    <div>
                      <strong>Híbrido:</strong> Taxa mensal base + custos por transação. Inclui{' '}
                      {formData.monthly_quota.toLocaleString()} pedidos, depois cobra $
                      {formData.per_transaction_price} por pedido adicional.
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="monthly_quota"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Quota Mensal *
                  </label>
                  <input
                    id="monthly_quota"
                    type="number"
                    required
                    min="0"
                    className="input"
                    value={formData.monthly_quota}
                    onChange={(e) =>
                      setFormData({ ...formData, monthly_quota: parseInt(e.target.value) })
                    }
                  />
                </div>

                <div>
                  <label
                    htmlFor="rate_limit_per_minute"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Limite de Débito (por minuto) *
                  </label>
                  <input
                    id="rate_limit_per_minute"
                    type="number"
                    required
                    min="1"
                    className="input"
                    value={formData.rate_limit_per_minute}
                    onChange={(e) =>
                      setFormData({ ...formData, rate_limit_per_minute: parseInt(e.target.value) })
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="monthly_price"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Preço Mensal ($) *
                  </label>
                  <input
                    id="monthly_price"
                    type="number"
                    required={
                      formData.pricing_model === 'monthly' || formData.pricing_model === 'hybrid'
                    }
                    min="0"
                    step="0.01"
                    className="input"
                    value={formData.monthly_price}
                    onChange={(e) =>
                      setFormData({ ...formData, monthly_price: parseFloat(e.target.value) || 0 })
                    }
                    disabled={formData.pricing_model === 'per_transaction'}
                  />
                  {formData.pricing_model === 'per_transaction' && (
                    <p className="text-xs text-gray-500 mt-1">
                      Não se aplica ao modelo por transação
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="per_transaction_price"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Preço por Transação ($) *
                  </label>
                  <input
                    id="per_transaction_price"
                    type="number"
                    required={
                      formData.pricing_model === 'per_transaction' ||
                      formData.pricing_model === 'hybrid'
                    }
                    min="0"
                    step="0.0001"
                    className="input"
                    value={formData.per_transaction_price}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        per_transaction_price: parseFloat(e.target.value) || 0,
                      })
                    }
                    disabled={formData.pricing_model === 'monthly'}
                  />
                  {formData.pricing_model === 'monthly' && (
                    <p className="text-xs text-gray-500 mt-1">
                      Não se aplica ao modelo mensal fixo
                    </p>
                  )}
                </div>
              </div>

              {/* Advanced Billing Section (Fintech) */}
              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-md font-medium text-gray-900 mb-3">
                  Faturação Avançada (Fintech)
                </h4>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Taxa Percentual (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      className="input"
                      value={formData.percentage_fee}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          percentage_fee: parseFloat(e.target.value) || 0,
                        })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Taxa Mínima ($)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="input"
                      value={formData.min_transaction_fee}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          min_transaction_fee: parseFloat(e.target.value) || 0,
                        })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Taxa Máxima ($)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="input"
                      value={formData.max_transaction_fee}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          max_transaction_fee: parseFloat(e.target.value) || 0,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Feature Limits Section */}
              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-md font-medium text-gray-900 mb-3">
                  Limites de Negócio (Feature Limits)
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Máximo de Utilizadores (0 = Ilimitado)
                    </label>
                    <input
                      type="number"
                      min="0"
                      className="input"
                      value={formData.max_users}
                      onChange={(e) =>
                        setFormData({ ...formData, max_users: parseInt(e.target.value) || 0 })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Volume Máximo (USD) (0 = Ilimitado)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      className="input"
                      value={formData.max_volume_usd}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          max_volume_usd: parseFloat(e.target.value) || 0,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button type="button" onClick={closeModal} className="btn-secondary flex-1">
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="btn-primary flex-1 disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'A guardar...'
                    : editingPlan
                      ? 'Atualizar Plano'
                      : 'Criar Plano'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
